import * as React from 'react'
import styled, { css } from 'styled-components'

import LinkButton from '../LinkButton/LinkButton'
import Icon from '../Icon/Icon'

import media from '../../../services/generateMediaQueries'

const SectionTitle = styled.h1`
  font-size: 1.375rem;
  font-family: var(--font-family-ayala-display-sans);
  font-weight: var(--font-weight-boldest);
  line-height: 1.75rem;
  color: ${props =>
    props.reverse && props.tertiary
      ? 'var(--color-tertiary)'
      : props.reverse
      ? 'var(--color-secondary)'
      : 'var(--color-primary)'};
  letter-spacing: var(--letter-spacing-wide);
  margin: 0;
  margin-bottom: 1.375rem;

  ${media.lessThan('medium')`
    font-size: 30px;
    line-height: 34px;
    margin-bottom: 16px;
    text-align: center;
  `}
`

const SectionDescription = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.95);
  padding: 45px 33px;
  border-radius: var(--border-radius-card);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 10px;
  margin-left: 10px;
  box-shadow: var(--box-shadow-large);

  ${media.lessThan('medium')`
    width: 100%;
    margin: 0;
    background: none;
    box-shadow: unset;

    ${props =>
      props.lightOnSmall &&
      css`
        color: #ffffff;
      `}

    align-items: center;
    justify-content: center;
  `}
`

const SectionText = styled.p`
  font-size: 1rem;
  line-height: 1.375rem;
  letter-spacing: var(--letter-spacing-wide);
  margin: 0;
  flex: 1;

  ${media.lessThan('medium')`
    font-size: 18px;
    line-height: 26px;
    margin-bottom: 40px;
    text-align: center;
    flex: unset;
  `}
`

const ButtonNav = styled.button`
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  outline: none;
  margin-right: 0.5rem;
  border: 0;
  font-size: 1.8rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-fg-lighter);
  cursor: pointer;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: var(--box-shadow);
  transition: all var(--animation-duration) var(--animation-easing);

  & > svg {
    margin-top: -5px;
    margin-left: ${props => (props.right ? 0 : '-5px')};
    margin-right: ${props => (props.right ? '-5px' : 0)};
  }

  :last-child {
    margin-right: 0;
  }

  :hover {
    background: var(--color-primary-hover);
    border-color: transparent;
    color: white;
    transition: all var(--animation-duration) var(--animation-easing);
  }

  :focus {
    border-color: var(--color-primary);
    transition: all var(--animation-duration) var(--animation-easing);
  }
`

const StyledLinkButton = styled(LinkButton)`
  flex: 0 0 auto;

  ${media.lessThan('medium')`
    font-size: 12px;
    /* margin-bottom: 48px; */
    height: 32px;
  `}
`

const StyledLink = styled.a`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  outline: 0;
  text-decoration: none;
  height: 2.25rem;
  font-size: 0.75rem;
  padding: 0 1.25rem;
  border-radius: 3px;
  font-weight: var(--font-weight-boldest);
  cursor: pointer;
  transtion-property: background-color, border-color, color, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);
  background-color: #e85e46;
  color: #ffffff !important;
  border: 1px solid #e85e46;

  ${media.lessThan('medium')`
    font-size: 12px;
    /* margin-bottom: 48px; */
    height: 32px;
  `}
`

const StyledButton = styled.div`
  flex: 0 0 auto;

  ${media.lessThan('medium')`
    font-size: 12px;
    /* margin-bottom: 48px; */
    height: 32px;
  `}

  display: inline-flex;
  align-items: center;
  outline: 0;
  text-decoration: none;

  height: ${props => (props.small ? '2.25rem' : '3rem')};
  font-size: ${props => (props.small ? '0.75rem' : '1rem')};
  padding: 0 ${props => (props.small ? '1.25rem' : '2rem')};
  border-radius: ${props =>
    props.small ? '3px' : 'var(--border-radius-button)'};
  font-weight: var(--font-weight-boldest);
  cursor: pointer;

  transition-property: background-color, border-color, color, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  ${props => {
      const { variant } = props

      let fgColor
      let bgColor
      let borderColor
      let bgColorHover
      let borderColorHover

      switch (variant) {
        case 'primary':
          bgColor = 'var(--color-primary)'
          borderColor = bgColor
          borderColorHover = borderColor
          bgColorHover = 'var(--color-primary-hover)'
          fgColor = '#ffffff'
          break
        case 'secondary':
          bgColor = 'var(--color-secondary)'
          borderColor = bgColor
          borderColorHover = borderColor
          bgColorHover = 'var(--color-secondary-hover)'
          fgColor = '#ffffff'
          break
        case 'tertiary':
          bgColor = 'var(--color-tertiary)'
          borderColor = bgColor
          borderColorHover = borderColor
          bgColorHover = 'var(--color-tertiary-hover)'
          fgColor = '#ffffff'
          break
        case 'facebook':
          bgColor = '#3b5998'
          borderColor = bgColor
          borderColorHover = borderColor
          bgColorHover = bgColor
          fgColor = '#ffffff'
          break
        case 'google':
          bgColor = '#ea4335'
          borderColor = bgColor
          borderColorHover = borderColor
          bgColorHover = bgColor
          fgColor = '#ffffff'
          break
        default:
          bgColor = 'var(--color-background-primary)'
          bgColorHover = bgColor
          fgColor = 'var(--color-primary)'
          borderColor = fgColor
          borderColorHover = borderColor
          break
      }

      return css`
        background-color: ${bgColor};
        color: ${fgColor} !important;
        border: 1px solid ${borderColor};

        &:hover {
          background-color: ${bgColorHover};
          border-color: ${borderColorHover};
        }

        &:disabled {
          background-color: ${bgColor};
          border-color: ${borderColor};
        }
      `
    }}
    :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledSection = styled.section`
  position: relative;
  max-width: 1024px;
  display: flex;
  min-height: 327.5px;
  margin: 3rem auto;

  padding-top: ${props => (props.sectionTitle === 'Explore' ? '90px' : '0')};

  ${props =>
    !props.default &&
    css`
      &:last-of-type {
        ${SectionTitle} {
          color: var(--color-accent-4);
        }
        ${StyledLinkButton} {
          background: var(--color-accent-4);
          border-color: var(--color-accent-4);
        }
        ${StyledButton} {
          background: var(--color-accent-4);
          border-color: var(--color-accent-4);
        }
        ${ButtonNav} {
          background: #ffffff;

          :hover {
            background: var(--color-primary);
            border-color: transparent;
            color: white;
            transition: all var(--animation-duration) var(--animation-easing);
          }

          :focus {
            border-color: var(--color-primary);
            transition: all var(--animation-duration) var(--animation-easing);
          }
        }
      }
      &:first-of-type {
        ${media.lessThan('medium')`
        ${SectionTitle} {
          color: #ffffff;
        }
        ${SectionText} {
          color: #ffffff;
        }
        ${StyledLinkButton} {
          background-color: var(--color-primary);
          color: #ffffff;
          border: 1px solid var(--color-primary);

          &:hover {
            background-color: var(--color-primary-hover);
            border-color: var(--color-primary);
          }

          &:disabled {
            background-color: var(--color-primary);
            border-color: var(--color-primary);
          }
        }
      `}
      }
    `}

    ${props =>
      props.pasyal &&
      css`
        min-height: 450px;
        ${SectionTitle} {
          color: #e85e46;
        }
      `}

  ${props =>
    props.reverse &&
    css`
      flex-direction: row-reverse;
    `}

  ${media.lessThan('medium')`
    display: block;
    margin: 2rem auto;
  `}
`

const SectionButtonContainer = styled.div`
  position: absolute;
  bottom: -22.5px;
  left: 36px;

  ${media.lessThan('medium')`
    display: none;
  `}
`

const SectionContent = styled.div`
  position: relative;
  height: ${props => (props.contentType === 'small' ? '327.5px' : '400px')};
  overflow: visible;
  flex: 1;

  ${props =>
    props.direction &&
    css`
      direction: ${props.direction};
    `}

  ${props =>
    props.contentType !== 'movie' &&
    css`
      ${media.lessThan('medium')`
      height: 327.5px;
    `}
    `}

  ${props =>
    props.isDisabled &&
    css`
      margin: auto;
    `}

    ${props =>
      props.pasyal &&
      css`
        ${media.lessThan('medium')`
      height: 420px;
    `}
      `}

`

const SliderOverflowContainer = styled.div`
  width: 4000px;
  position: absolute;
  overflow: hidden;

  ${media.lessThan('medium')`
    overflow: visible;
    position: relative;
    width: unset;
  `}
`

const SliderCompactContainer = styled.div`
  width: 320px;

  overflow: visible;
  direction: ltr !important;

  ${media.lessThan('medium')`
    width: unset;
  `}
`

const Section = ({
  sectionTitle,
  sectionText,
  sectionContentType = 'default',
  to,
  onClick = () => {},
  linkLabel = 'View all',
  reverse,
  tertiary,
  sliderPrev,
  sliderNext,
  contentDirection,
  children,
  onClickOnly,
  isDisabled,
  default: defaultProp,
  pasyal,
}) => {
  return (
    <StyledSection
      default={defaultProp}
      reverse={reverse}
      pasyal={pasyal}
      sectionTitle={sectionTitle}
    >
      <SectionDescription>
        <SectionTitle reverse={reverse} tertiary={tertiary}>
          {sectionTitle}
        </SectionTitle>
        <SectionText>{sectionText}</SectionText>
        {onClickOnly ? (
          <StyledButton
            variant={
              reverse && tertiary
                ? 'tertiary'
                : reverse
                ? 'secondary'
                : 'primary'
            }
            small
            onClick={e => onClick(e, sectionTitle)}
          >
            {linkLabel}
          </StyledButton>
        ) : (
          <>
            {to === 'pasyal' ? (
              <>
                <StyledLink
                  href={process.env.REACT_APP_PASYAL_LINK}
                  target="_blank"
                >
                  {linkLabel}
                </StyledLink>
              </>
            ) : (
              <StyledLinkButton
                to={to || '/'}
                variant={
                  reverse && tertiary
                    ? 'tertiary'
                    : reverse
                    ? 'secondary'
                    : 'primary'
                }
                small
                onClick={e => onClick(e, sectionTitle)}
              >
                {linkLabel}
              </StyledLinkButton>
            )}
          </>
        )}
        <SectionButtonContainer>
          <ButtonNav onClick={sliderPrev}>
            <Icon name="slide-left" />
          </ButtonNav>
          <ButtonNav onClick={sliderNext} right>
            <Icon name="slide-right" />
          </ButtonNav>
        </SectionButtonContainer>
      </SectionDescription>
      <div>
        <SectionContent
          direction={contentDirection}
          contentType={sectionContentType}
          isDisabled={isDisabled}
          pasyal={pasyal}
        >
          <SliderOverflowContainer>
            <SliderCompactContainer>{children}</SliderCompactContainer>
          </SliderOverflowContainer>
        </SectionContent>
      </div>
    </StyledSection>
  )
}

Section.defaultProps = {
  // TODO: specify default props
}

Section.propTypes = {
  // TODO: specify prop types
}

export default Section
